import { SLOT_IDS, type SlotId } from "../../utils/hangingProtocolService";
import type { ResliceTarget } from "../../atoms/hangingProtocolAtoms";
import { resolveNewViewportFlagFrom } from "./resolveNewViewportFlag";
import { logger } from "../../utils/logger";

// `ResliceTarget` is state (it lives on `SlotState.reslice`), so it's declared
// in the atoms module. Re-exported here so `ProtocolViewport`'s existing
// `import type { ResliceTarget } from "./resolveGridReslice"` keeps working.
export type { ResliceTarget };

export interface GridResliceArgs {
  slotStates: Record<SlotId, { reslice?: ResliceTarget | null }>;
  isSegmentationProject: boolean;
  newViewportParam: string | null;
}

/**
 * Which panes render a reformat, and whether the grid takes the unified
 * viewport path.
 *
 * Reformats are EXPLICIT. A pane reslices only because the user asked for it
 * (series right-click → plane), recorded as `reslice` on that slot. There
 * is deliberately NO metadata inference here: the previous `planReslices` filled
 * every empty cor/sag slot from the largest axial, which is why reformats showed
 * up on studies nobody had asked about (2026-07-24 review).
 *
 * `unified` is now the default for every project, segmentation or not — it no
 * longer depends on whether a reformat was requested. Before this, `unified`
 * flipped false→true the moment the FIRST reformat of a case was requested,
 * remounting all four panes mid-session (observed live: it reset a pane's zoom
 * from 96% to 100%).
 *
 * Defaulting unified on for every non-segmentation study too is a DELIBERATE,
 * product-owner-approved change — NOT a no-op — for series-rich studies. The
 * old `planReslices` only resliced an empty cor/sag slot, but
 * `assignSeriesToSlots` Pass B (hangingProtocolService.ts:612) fills every
 * empty slot with a leftover series on non-segmentation projects, so a study
 * with 4+ usable series left nothing to reslice: `wantsReslice` was false and
 * staging runs the LEGACY per-pane renderer on those studies today. This
 * branch switches them to the unified `SessionVolumeMount` path unconditionally.
 * That's justified by two things staying constant either way: no mid-session
 * false→true flip that remounts all four panes, and #844 progressive loading +
 * the #575 shared workspace now apply to every non-segmentation surface
 * instead of only the series-poor ones that happened to trigger a reslice. So
 * both branches below pass `true` into `resolveNewViewportFlagFrom`; only
 * `?newViewport=0` can still turn it off.
 *
 * Reslice rendering lives only on the unified `SessionVolumeMount` path.
 * `?newViewport=0` pins legacy and therefore drops any requested reformat —
 * rendering the source axial inside a pane labelled coronal would be a silent
 * wrong answer. That drop is logged (only when a reformat was actually
 * requested — the ordinary "nothing requested" path stays quiet).
 */
export function resolveGridReslice(args: GridResliceArgs): {
  unified: boolean;
  resliceBySlot: Partial<Record<SlotId, ResliceTarget>>;
} {
  const { slotStates, isSegmentationProject, newViewportParam } = args;

  // The segmentation layout mounts native cor/sag panes that nnInteractive
  // seeds; a reslice pane disables segmentation on its plane, so reformats stay
  // out of that layout entirely.
  if (isSegmentationProject) {
    return { unified: resolveNewViewportFlagFrom(true, newViewportParam), resliceBySlot: {} };
  }

  const requested: Partial<Record<SlotId, ResliceTarget>> = {};
  for (const slot of SLOT_IDS) {
    const { reslice } = slotStates[slot];
    if (reslice) {
      requested[slot] = reslice;
    }
  }

  // `true` here (not "reformat requested") — see doc comment above.
  const unified = resolveNewViewportFlagFrom(true, newViewportParam);
  if (!unified && Object.keys(requested).length > 0) {
    // `?newViewport=0` pins the legacy per-pane renderer, which cannot
    // reslice. Rendering the source axial under the requested plane's label
    // would be a silent wrong answer, so the request is dropped instead — log
    // why so a reader's "the reformat did nothing" report is traceable.
    logger.warn(
      "[resolveGridReslice] dropped reformat request(s) — legacy path pinned by ?newViewport=0 cannot reslice",
      { slots: Object.keys(requested) },
    );
  }
  return { unified, resliceBySlot: unified ? requested : {} };
}
